import type { ServicePageConfig } from "../../../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../../../package-pricing";
import { getCityMunicipalityList } from "../../../cities";

const stockholmKommunerText = getCityMunicipalityList("stockholm");

export const flytthjalpMarstaPage: ServicePageConfig = {
  slug: "flytthjalp-marsta",
  serviceCategory: "flytt",
  city: "stockholm",

  meta: {
    title: "Flytthjälp Märsta – Flytt i hela Sigtuna kommun | Ungdomskraft",
    description:
      "Boka flytthjälp i Märsta och Sigtuna kommun. Skåpbil och bärare i Valsta, Tingvalla, Ekilla, Steninge, Sigtuna stad och Rosersberg. Fast pris, RUT.",
    h1: "Flytthjälp Märsta",
  },

  hero: {
    heading: "Flytthjälp i Märsta och Sigtuna kommun",
    intro:
      "Vi flyttar hem i hela Sigtuna kommun – från hyreslägenheten i Valsta eller Tingvalla och radhuset i Steningehöjden till villan i Ekilla, Norrbacka eller Sigtuna stad. Ungdomskraft kommer med skåpbil och bärare, lastar, kör och bär in på nya adressen. Priset ser du när du bokar och RUT-avdraget dras direkt.",
    heroImage: "/hero-flytthjalp.png",
    heroImageAlt:
      "Flytthjälp i Märsta – Ungdomskraft lastar en skåpbil utanför ett flerbostadshus",
  },

  featureCards: {
    sinceText: "Flytthjälp sedan 2014",
    storyText:
      "Ungdomskraft har flyttat hushåll i Stockholmsregionen sedan 2014. I Märsta möter vi allt från hisslösa trapphus i Valsta till villatomter ner mot Mälaren, och våra bärare vet hur man får en tung byrå både nerför trappan och in i en full skåpbil utan skador.",
    bookYourText: "Boka flytten i Märsta",
    bookDescText:
      "Lägg in adresserna, beskriv bohaget och välj en tid som passar. Du ser priset direkt, bil och bärare ingår.",
    vehicleTitleText: "Skåpbil från dörr till dörr",
    vehicleDescText:
      "Vi kör själva mellan adresserna, oavsett om du flyttar inom Märsta, till Sigtuna stad eller in till Stockholm. Du slipper hyra bil, tanka och lämna tillbaka den i tid.",
  },

  itemsSection: {
    heading: "Vanliga saker vi flyttar i Märsta",
    items: [
      {
        name: "Soffor & fåtöljer",
        description: "Soffgrupper och fåtöljer, inlindade i filt för trapphuset",
        image: "/sofa.webp",
        alt: "Soffa som bärs vid flytthjälp i Valsta, Märsta",
      },
      {
        name: "Sängar & madrasser",
        description: "Dubbelsängar, ramsängar och madrasser i skyddsplast",
        image: "/bed.webp",
        alt: "Säng som flyttas vid flytt i Märsta, Sigtuna kommun",
      },
      {
        name: "Bord & stolar",
        description: "Köksbord, matgrupper, skrivbord och kontorsstolar",
        image: "/table.webp",
        alt: "Köksbord som lastas vid flytt i Tingvalla, Märsta",
      },
      {
        name: "Vitvaror",
        description: "Tvättmaskin, kyl och frys som kopplas loss och säkras",
        image: "/washer.webp",
        alt: "Tvättmaskin som flyttas vid flytthjälp i Sigtuna kommun",
      },
      {
        name: "Garderober & byråer",
        description: "Garderober, byråer, hyllor och vitrinskåp",
        image: "/wardrobe.webp",
        alt: "Garderob som bärs vid villaflytt i Ekilla, Märsta",
      },
      {
        name: "Flyttkartonger",
        description: "Kartonger, lådor och förrådssaker från källare och vind",
        image: "/box.webp",
        alt: "Flyttkartonger som bärs till skåpbil vid flytt i Märsta",
      },
    ],
  },

  seoContent: {
    mainHeading: "Flytthjälp i Märsta – lägenhet, radhus och villa i Sigtuna kommun",
    mainText:
      "Märsta är centralorten i Sigtuna kommun, och bostäderna här är av flera slag. Valsta består till stor del av flerbostadshus från miljonprogramsåren och även Tingvalla är mest lägenheter, så flytten ofta avgörs av trapphuset och hur nära porten bilen kan stå. Kring stationen i Sätuna bor många i lägenheter nära Märsta centrum. I Ekilla, Norrbacka och Steninge dominerar villor och radhus, och där handlar flytten mer om mängd: garage, förråd och tunga möbler som ska ut ur hus med flera plan.\n\nArlanda ligger några minuter bort och är en av regionens största arbetsplatser, vilket gör att många flyttar till Märsta för jobbets skull. Andra kommer för priserna och pendeltåget: Märsta är ändstation på pendeltågslinjen mot Stockholm City, och Rosersberg har en egen station längs samma spår. Vanliga flyttar hos oss är unga som lämnar föräldrahemmet för en första lägenhet i Valsta, familjer som går från lägenhet till radhus i Steningehöjden och pensionärer som byter villa mot något mindre och närmare centrum.\n\nSigtuna kommun sträcker sig också bortom Märsta. Sigtuna stad vid Mälaren har äldre trähus och smala gator i stadskärnan, där skåpbilen måste planeras in noga, och Rosersberg har bostadskvarter nära pendeltåget. Med E4 strax utanför Märsta och Märstavägen genom kommunen tar vi oss snabbt mellan kommundelarna, och flyttar du söderut når vi Upplands Väsby, Sollentuna och Stockholm utan krångel. Vi planerar alltid lastplats, antal bärare och antal vändor innan vi åker, så att dagen går som den ska.",
    preparationHeading: "Tips inför flytten i Märsta",
    preparationText:
      "Börja med förrådet och vinden, det är där det brukar ta längst tid. Packa tunga saker som böcker i små kartonger och lätta saker i stora, så blir varje kartong lagom att bära. Märk varje kartong med rum. Möbler som behöver tas isär kan vi demontera på plats, men har du redan skruvat isär något, samla beslagen i en märkt påse.\n\nFlyttar du från ett hyreshus i Valsta eller Tingvalla, ta reda på var närmaste lastplats eller gästparkering finns och om det är långt från porten. I villa- och radhusområdena i Ekilla, Norrbacka och Steninge räcker det oftast att uppfarten är tom. Flyttar du in i Sigtunas stadskärna, berätta det i bokningen så att vi kan planera var bilen kan stå på de smala gatorna. Frosta av frysen dygnet innan och ha nycklar och portkoder redo för båda adresserna.",
    additionalSections: [
      {
        heading: "Kommundelar i Sigtuna kommun där vi hjälper till att flytta",
        content:
          "Vi flyttar i hela Märsta: Valsta, Tingvalla, Sätuna, Norrbacka, Ekilla, Steninge och Steningehöjden. I flerbostadshusen bär vi både med och utan hiss, och i villa- och radhusområdena tar vi hela huset med garage och förråd.\n\nUtanför Märsta hjälper vi till i Sigtuna stad, i Rosersberg och i de mindre orterna i kommunen. Flyttar du mellan till exempel Rosersberg och Sigtuna stad kör vi hela vägen i samma bokning.\n\nKörsträcka inom 20 km ingår. Går flytten längre, till exempel från Märsta in till Stockholm, räknas resterande kilometer enligt paketets pris och syns direkt i bokningen.",
      },
      {
        heading: "Flytta till Märsta från Stockholm",
        content:
          "Många av våra kunder i Märsta kommer från en lägenhet längre söderut i länet. Vi hämtar bohaget där du bor i dag, kör norrut på E4 och bär in allt i det nya hemmet samma dag. Har du saker kvar som inte ska med till Märsta kan vi ta dem till återvinning i samma veva.\n\nFlytthjälp ger rätt till RUT-avdrag: 50 % av arbetskostnaden, upp till 75 000 kr per person och år. Vi drar av det direkt på fakturan.",
        cta: { text: "Se bortforsling i Märsta →", href: "/bortforsling-marsta" },
      },
      {
        heading: "Varför boka flytthjälp i Märsta med Ungdomskraft?",
        listItems: [
          {
            label: "Hela Sigtuna kommun –",
            text: "Vi flyttar i Märsta, Sigtuna stad och Rosersberg och vet hur det ser ut vid portarna i Valsta lika väl som på villagatorna i Ekilla.",
          },
          {
            label: "Snabbt på plats –",
            text: "E4 går strax förbi Märsta, och vi har bilar i hela Stockholmsregionen. Lediga tider finns ofta samma vecka.",
          },
          {
            label: "Fast pris från början –",
            text: "Priset syns i bokningen innan du bekräftar. Inga tillägg för trappor och inget offertkrångel.",
          },
          {
            label: "Trygg Hansa-försäkrat –",
            text: "Bohaget, trapphuset och huset vi bär i är försäkrade under hela flytten.",
          },
          {
            label: "Rätt utrustning –",
            text: "Möbelfiltar, bärremmar, spännband och skyddsmattor följer med varje bil.",
          },
        ],
      },
    ],
  },

  faq: [
    {
      question: "Vad kostar flytthjälp i Märsta?",
      answer:
        "Priset beror på bohagets storlek. Enstaka möbler bokas som Liten (995 kr) eller Mellan (1 495 kr), en etta eller tvåa med fullt bohag hamnar ofta på 2 495–4 995 kr och en hel villa eller ett radhus bokas som Mega Stor (8 995 kr). 20 km körning ingår och du ser priset innan du bokar.",
    },
    {
      question: "Hjälper ni till med flytt i hela Sigtuna kommun?",
      answer:
        "Ja. Vi flyttar i Märsta, Sigtuna stad, Rosersberg och övriga delar av kommunen, utan tillägg för någon kommundel.",
    },
    {
      question: "Får jag RUT-avdrag på flytthjälpen?",
      answer:
        "Ja, du får 50 % avdrag på arbetskostnaden, upp till 75 000 kr per person och år. Vi drar av det direkt på fakturan.",
    },
    {
      question: "Hur snabbt kan ni flytta mig i Märsta?",
      answer:
        "Vi har ofta tider samma vecka, ibland samma dag. Välj datum i bokningen så ser du vad som är ledigt.",
    },
    {
      question: "Bär ni i hus utan hiss i Valsta och Tingvalla?",
      answer:
        "Ja, vi bär i trapphus med och utan hiss. Det kostar inget extra att bo högt upp.",
    },
    {
      question: "Kan ni flytta en villa i Ekilla eller Steninge?",
      answer:
        "Ja. Vi tar hela huset inklusive garage, förråd och trädgårdsmöbler och gör fler vändor om allt inte ryms på en gång.",
    },
    {
      question: "Vad kostar det att flytta från Märsta till Stockholm?",
      answer:
        "De första 20 km ingår. Sträckan Märsta–Stockholm är längre, så resterande kilometer debiteras enligt paketets km-pris, som du ser i pristabellen och i bokningen.",
    },
    {
      question: "Kan ni flytta i Sigtunas gamla stadskärna?",
      answer:
        "Ja. Gatorna är smala, så skriv i bokningen att adressen ligger i stadskärnan. Då planerar vi var bilen kan stå och hur långt vi behöver bära.",
    },
    {
      question: "Tar ni isär och monterar möbler?",
      answer:
        "Ja, vi demonterar sängar, garderober och bord som inte går ut hela och monterar dem igen i nya hemmet.",
    },
    {
      question: "Flyttar ni tvättmaskin och kylskåp?",
      answer:
        "Ja, vi kopplar loss, bär och säkrar vitvarorna i bilen. Frosta av kyl och frys minst ett dygn innan flytten.",
    },
    {
      question: "Kan jag boka flytt på helgen?",
      answer: "Ja, vi flyttar sju dagar i veckan, även lördag och söndag.",
    },
    {
      question: "Kan ni flytta kontor eller personalbostäder nära Arlanda?",
      answer:
        "Ja, vi gör mindre kontorsflyttar och flyttar möbler mellan lägenheter i Märsta och kring Arlanda. Beskriv uppdraget i bokningen så väljer vi rätt paket.",
    },
    {
      question: "Vilka kommuner mer flyttar ni i?",
      answer: `Vi hjälper till med flytt i hela Stockholmsregionen: ${stockholmKommunerText}. Körsträcka inom 20 km ingår alltid.`,
    },
  ],

  priceHint: PACKAGE_PRICE_HINT_SV.transport,

  pricingRows: getPackagePricingRows("transport", "sv"),

  dynamicHeadings: {
    threeSteps: "Så går flytten i Märsta till – 3 steg",
    step2Text:
      "Vi kommer med skåpbilen, lindar in möblerna, bär ut och lastar. Sedan kör vi till din nya adress, i Märsta, Sigtuna eller längre bort.",
    step3Title: "Klart i nya hemmet",
    step3Desc:
      "Allt bärs in och ställs i rätt rum. Det som togs isär sätts ihop igen innan vi åker.",
    ctaHeading: "Boka flytthjälp i Märsta nu",
    ctaText:
      "Ange adresserna, välj paket och tid. Du ser priset direkt – skåpbil och bärare ingår och RUT dras på fakturan.",
  },

  en: {
    slug: "moving-help-marsta",
    meta: {
      title: "Moving Help Märsta – Moves across Sigtuna | Ungdomskraft",
      description:
        "Book moving help in Märsta and Sigtuna municipality. Van and movers in Valsta, Tingvalla, Ekilla, Steninge, Sigtuna town and Rosersberg. Fixed price.",
      h1: "Moving Help Märsta",
    },
    hero: {
      heading: "Moving Help in Märsta and Sigtuna Municipality",
      intro:
        "We move homes across Sigtuna municipality — from a rental flat in Valsta or Tingvalla and a terraced house in Steningehöjden to a detached house in Ekilla, Norrbacka or Sigtuna town. Ungdomskraft arrives with a van and movers, loads, drives and carries everything in at the new address. You see the price when you book and the RUT deduction comes off straight away.",
      heroImageAlt:
        "Moving help in Märsta — Ungdomskraft loading a van outside an apartment block",
    },
    featureCards: {
      sinceText: "Moving help since 2014",
      storyText:
        "Ungdomskraft has moved households in the Stockholm region since 2014. In Märsta we meet everything from lift-less stairwells in Valsta to house plots running down towards Lake Mälaren, and our movers know how to get a heavy dresser down the stairs and into a full van without damage.",
      bookYourText: "Book your Märsta move",
      bookDescText:
        "Add the addresses, describe your household and pick a time that suits you. You see the price instantly, van and movers included.",
      vehicleTitleText: "A van from door to door",
      vehicleDescText:
        "We drive between the addresses ourselves, whether you're moving within Märsta, to Sigtuna town or into Stockholm. No van to rent, refuel and return on time.",
    },
    itemsSection: {
      heading: "Common items we move in Märsta",
      items: [
        {
          name: "Sofas & armchairs",
          description: "Sofa sets and armchairs, wrapped in blankets for the stairwell",
          image: "/sofa.webp",
          alt: "Sofa carried during moving help in Valsta, Märsta",
        },
        {
          name: "Beds & mattresses",
          description: "Double beds, bed frames and mattresses in protective plastic",
          image: "/bed.webp",
          alt: "Bed being moved during a move in Märsta, Sigtuna municipality",
        },
        {
          name: "Tables & chairs",
          description: "Kitchen tables, dining sets, desks and office chairs",
          image: "/table.webp",
          alt: "Kitchen table loaded during a move in Tingvalla, Märsta",
        },
        {
          name: "Appliances",
          description: "Washers, fridges and freezers disconnected and secured",
          image: "/washer.webp",
          alt: "Washing machine moved during moving help in Sigtuna municipality",
        },
        {
          name: "Wardrobes & dressers",
          description: "Wardrobes, dressers, shelving and display cabinets",
          image: "/wardrobe.webp",
          alt: "Wardrobe carried during a house move in Ekilla, Märsta",
        },
        {
          name: "Moving boxes",
          description: "Boxes, crates and storage items from basement and loft",
          image: "/box.webp",
          alt: "Moving boxes carried to a van during a move in Märsta",
        },
      ],
    },
    seoContent: {
      mainHeading: "Moving help in Märsta — flats, terraced houses and villas in Sigtuna municipality",
      mainText:
        "Märsta is the main town of Sigtuna municipality, and its housing comes in several kinds. Valsta is largely apartment blocks from the Million Programme years and Tingvalla is mostly flats too, so a move is often decided by the stairwell and how close to the entrance the van can park. Around the station in Sätuna many people live in flats near Märsta centrum. In Ekilla, Norrbacka and Steninge detached and terraced houses dominate, and there the move is more about volume: garage, storage and heavy furniture coming out of houses with several floors.\n\nArlanda is a few minutes away and one of the region's biggest workplaces, so many people move to Märsta for work. Others come for the prices and the commuter train: Märsta is the terminus of the commuter line to Stockholm City, and Rosersberg has its own station on the same tracks. Typical moves for us are young people leaving home for a first flat in Valsta, families going from a flat to a terraced house in Steningehöjden and retirees swapping a house for something smaller closer to the centre.\n\nSigtuna municipality also reaches beyond Märsta. Sigtuna town on Lake Mälaren has older wooden houses and narrow streets in the old centre, where the van has to be planned in carefully, and Rosersberg has residential blocks close to the commuter train. With the E4 just outside Märsta and Märstavägen running through the municipality we move quickly between districts, and if you're moving south we reach Upplands Väsby, Sollentuna and Stockholm without hassle. We always plan the loading spot, the number of movers and the number of trips before we set off, so the day goes to plan.",
      preparationHeading: "Tips before your move in Märsta",
      preparationText:
        "Start with the storage room and loft — that's usually what takes longest. Pack heavy things like books in small boxes and light things in large ones, so every box is a sensible weight. Label each box by room. We can dismantle furniture on site, but if you've already taken something apart, keep the fittings in a labelled bag.\n\nMoving out of a rental block in Valsta or Tingvalla? Find out where the nearest loading spot or guest parking is and whether it's far from the entrance. In the house areas of Ekilla, Norrbacka and Steninge a clear driveway is usually enough. If you're moving into Sigtuna's old town, mention it in the booking so we can plan where the van can stand on the narrow streets. Defrost the freezer the day before and have keys and door codes ready for both addresses.",
      additionalSections: [
        {
          heading: "Districts in Sigtuna municipality where we help you move",
          content:
            "We move across all of Märsta: Valsta, Tingvalla, Sätuna, Norrbacka, Ekilla, Steninge and Steningehöjden. In apartment blocks we carry with or without a lift, and in the house areas we take the whole house with garage and storage.\n\nOutside Märsta we help in Sigtuna town, in Rosersberg and in the smaller villages of the municipality. Moving between, say, Rosersberg and Sigtuna town? We drive the whole way in the same booking.\n\nDriving within 20 km is included. If the move goes further, for example from Märsta into Stockholm, the remaining kilometres are charged at the package rate and shown directly in the booking.",
        },
        {
          heading: "Moving to Märsta from Stockholm",
          content:
            "Many of our customers in Märsta come from a flat further south in the county. We collect your belongings where you live today, drive north on the E4 and carry everything into the new home the same day. If there are things that shouldn't come to Märsta, we can take them to recycling at the same time.\n\nMoving help qualifies for the RUT tax deduction: 50% of the labour cost, up to SEK 75,000 per person per year. We deduct it directly on the invoice.",
          cta: { text: "See junk removal in Märsta →", href: "/en/junk-removal-marsta" },
        },
        {
          heading: "Why book moving help in Märsta with Ungdomskraft?",
          listItems: [
            {
              label: "All of Sigtuna municipality –",
              text: "We move in Märsta, Sigtuna town and Rosersberg and know the entrances in Valsta as well as the house streets in Ekilla.",
            },
            {
              label: "Quickly on site –",
              text: "The E4 passes right by Märsta and we have vans across the Stockholm region. Slots are often available the same week.",
            },
            {
              label: "Fixed price from the start –",
              text: "The price appears in the booking before you confirm. No stair surcharge and no quote hassle.",
            },
            {
              label: "Insured by Trygg Hansa –",
              text: "Your belongings, the stairwell and the house we carry in are insured throughout the move.",
            },
            {
              label: "The right equipment –",
              text: "Furniture blankets, carrying straps, ratchet straps and floor protection come with every van.",
            },
          ],
        },
      ],
    },
    faq: [
      {
        question: "How much does moving help in Märsta cost?",
        answer:
          "The price depends on the size of your household. Single pieces are booked as Small (SEK 995) or Medium (SEK 1,495), a fully furnished studio or one-bed often lands at SEK 2,495–4,995 and a whole house is booked as Mega Large (SEK 8,995). 20 km of driving is included and you see the price before booking.",
      },
      {
        question: "Do you help with moves across Sigtuna municipality?",
        answer:
          "Yes. We move in Märsta, Sigtuna town, Rosersberg and the rest of the municipality, with no surcharge for any district.",
      },
      {
        question: "Do I get the RUT deduction on moving help?",
        answer:
          "Yes, you get 50% off the labour cost, up to SEK 75,000 per person per year. We deduct it directly on the invoice.",
      },
      {
        question: "How quickly can you move me in Märsta?",
        answer:
          "We often have slots the same week, sometimes the same day. Pick a date in the booking to see what's free.",
      },
      {
        question: "Do you carry in buildings without a lift in Valsta and Tingvalla?",
        answer: "Yes, we carry in stairwells with and without lifts. Living on a high floor costs nothing extra.",
      },
      {
        question: "Can you move a house in Ekilla or Steninge?",
        answer:
          "Yes. We take the whole house including garage, storage and garden furniture, and make more trips if it doesn't all fit at once.",
      },
      {
        question: "What does it cost to move from Märsta to Stockholm?",
        answer:
          "The first 20 km are included. Märsta to Stockholm is further, so the remaining kilometres are charged at the package's per-km rate, shown in the price table and the booking.",
      },
      {
        question: "Can you move in Sigtuna's old town?",
        answer:
          "Yes. The streets are narrow, so note in the booking that the address is in the old town. We'll then plan where the van can stand and how far we need to carry.",
      },
      {
        question: "Do you dismantle and reassemble furniture?",
        answer:
          "Yes, we take apart beds, wardrobes and tables that won't go out whole and reassemble them in your new home.",
      },
      {
        question: "Do you move washing machines and fridges?",
        answer:
          "Yes, we disconnect, carry and secure appliances in the van. Defrost the fridge and freezer at least a day before the move.",
      },
      {
        question: "Can I book a move at the weekend?",
        answer: "Yes, we move seven days a week, including Saturday and Sunday.",
      },
      {
        question: "Can you move offices or staff housing near Arlanda?",
        answer:
          "Yes, we do smaller office moves and move furniture between flats in Märsta and around Arlanda. Describe the job in the booking and we'll pick the right package.",
      },
      {
        question: "Which other municipalities do you move in?",
        answer: `We help with moves across the Stockholm region: ${stockholmKommunerText}. Driving within 20 km is always included.`,
      },
    ],
    priceHint: PACKAGE_PRICE_HINT_EN.transport,
    pricingRows: getPackagePricingRows("transport", "en"),
    dynamicHeadings: {
      threeSteps: "How your Märsta move works — 3 steps",
      step2Text:
        "We arrive with the van, wrap the furniture, carry out and load. Then we drive to your new address, in Märsta, Sigtuna or further afield.",
      step3Title: "Done in the new home",
      step3Desc:
        "Everything is carried in and placed in the right room. Anything taken apart is reassembled before we leave.",
      ctaHeading: "Book moving help in Märsta now",
      ctaText:
        "Enter the addresses, choose a package and a time. You see the price instantly — van and movers included, RUT deducted on the invoice.",
    },
  },
};
